import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import SettingsContainer, { SettingsSettingType } from 'components/settings/SettingsContainer';
import SecondaryButton from 'components/buttons/SecondaryButton';
import TicketEmailAddressList from './TicketEmailAddressList';
import '../../../style/scss/tickets.scss';

// The service desk settings page with the numbering, the email address and the forwarding address
const TicketSettings: React.FC = () => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { activeFeatures } = useAuthContext();
    const [copied, setCopied] = useState(false);

    // With divisions the addresses are managed per division in the list below the settings
    const divisionsActive = activeFeatures.includes('divisions');

    // Fetch the inbound address of the environment
    const { response } = useFetchData({ apiUrl: 'get_ticket_inbound_address' });
    const inboundAddress: string | undefined = response?.inbound_address;

    // Copy the inbound address to the clipboard
    const handleCopy = async () => {
        if (!inboundAddress) return;
        try {
            await navigator.clipboard.writeText(inboundAddress);
            setCopied(true);
            setFloatingAlert({ type: 'success', message: 'ticket.settings.address_copied_message' });
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Copying the address failed', error);
        }
    };

    // The settings of the page
    const settings: SettingsSettingType[] = [
        {
            title: 'ticket.settings.numbering_scheme_title',
            explanationText: 'ticket.settings.numbering_scheme_explanation',
            fields: [
                {
                    type: 'text',
                    label: 'settings.general.number_sequence_title',
                    name: 'ticket_numbering_scheme',
                    placeholder: '',
                    group: 'field-row',
                    width: '30%',
                    shouldAutoFocus: false
                },
                {
                    type: 'number',
                    label: 'settings.general.next_number_label',
                    name: 'ticket_current_number',
                    group: 'field-row',
                    width: '30%'
                }
            ]
        },
        ...(divisionsActive ? [] : [{
            title: 'ticket.settings.email_address_title',
            explanationText: 'ticket.settings.email_address_explanation',
            fields: [
                {
                    type: 'text',
                    label: 'ticket.settings.email_prefix_label',
                    name: 'ticket_email_prefix',
                    placeholder: 'ticket.settings.email_prefix_placeholder',
                    group: 'field-row',
                    width: '30%',
                    shouldAutoFocus: false
                },
                {
                    type: 'dropdown',
                    label: 'ticket.settings.email_domain_label',
                    name: 'ticket_email_domain',
                    apiRequest: [{
                        endpoint: 'get_email_domain_list',
                        object: 'email_domain',
                        params: { verified: true, deleted: false }
                    }],
                    disabled_selected: 'ticket.settings.email_domain_placeholder',
                    allowNoneOption: true,
                    showSearch: false,
                    optionFormat: 'domain',
                    selectionFormat: 'domain',
                    group: 'field-row',
                    width: '40%'
                }
            ]
        } as SettingsSettingType]),
        {
            title: 'ticket.settings.inbound_address_title',
            explanationText: 'ticket.settings.inbound_address_explanation',
            tooltipText: 'ticket.settings.inbound_address_steps',
            fields: [
                {
                    type: 'render',
                    render: () => (
                        <div className='inbound-address'>
                            <code>{inboundAddress || '...'}</code>
                            <SecondaryButton
                                label={copied ? 'ticket.settings.copied_label' : 'ticket.settings.copy_label'}
                                size='small'
                                onClick={handleCopy}
                            />
                        </div>
                    )
                }
            ]
        },
    ];

    return (
        <>
            <SettingsContainer settings={settings} />
            {divisionsActive && (
                <TicketEmailAddressList />
            )}
        </>
    );
};

export default TicketSettings;
